import { reactive, toRaw, watch } from 'vue';
import { CoreTypes, type Id, type ObjectRecord, type Style } from '@vp/shared-types';
import { pickStyle } from '@vp/render';
import { live, ui } from './bridge';
import { primary, supportsFill, supportsStroke, supportsText } from './selection';
import { settings } from '../platform';

/**
 * Copy style (format painter): the look of one object — fill, outline, opacity, corners, text style —
 * goes onto others. Ctrl+Alt+C / Ctrl+Alt+V, or the brush: the next object clicked takes the style
 * (Esc or a click on empty canvas cancels). "Make default style" keeps it for new objects of the type.
 */
export const painter = reactive({ style: null as Partial<Style> | null, armed: false, from: [] as Id[] });

export function copyStyle(): boolean {
  const o = primary.value;
  if (!o) return false;
  painter.style = pickStyle(o.style);
  return true;
}

const isLine = (o: ObjectRecord) => o.type === CoreTypes.line || o.type === CoreTypes.connector;

/** What of a copied style applies to an object (a fill never lands on a line, text style only where there is text). */
export function stylePatch(o: ObjectRecord, s: Partial<Style>): Partial<Style> | null {
  const p: Partial<Style> = {};
  if (s.fill && supportsFill(o)) p.fill = JSON.parse(JSON.stringify(s.fill)) as Style['fill'];
  if (s.stroke !== undefined && supportsStroke(o) && !(s.stroke === null && isLine(o))) p.stroke = s.stroke ? { ...s.stroke } : null;
  if (s.opacity !== undefined) p.opacity = s.opacity;
  if (s.cornerRadius !== undefined && (o.style.cornerRadius !== undefined || o.type === CoreTypes.rect || o.type === CoreTypes.sticker)) p.cornerRadius = s.cornerRadius;
  if (s.text && supportsText(o)) p.text = { ...s.text };
  return Object.keys(p).length ? p : null;
}

export function pasteStyle(): void {
  // the stored style is behind a reactive proxy, which cannot be cloned
  const s = painter.style ? toRaw(painter.style) : null;
  if (!s || ui.readOnly) return;
  live.editor?.updateStyles((o) => stylePatch(o, s));
}

/** The brush: copy now, apply to the next object the user picks. */
export function armPainter(): void {
  if (painter.armed) {
    painter.armed = false;
    return;
  }
  if (!copyStyle()) return;
  painter.armed = true;
  painter.from = [...ui.selection];
}

watch(
  () => ui.selection,
  (sel) => {
    if (!painter.armed) return;
    if (!sel.length) {
      painter.armed = false;
      return;
    }
    if (sel.length === painter.from.length && sel.every((id) => painter.from.includes(id))) return;
    painter.armed = false;
    pasteStyle();
  },
);

/** The type's own default style was replaced by the user's. */
export function hasDefaultStyle(type: string): boolean {
  return !!settings.editor.defaultStyles?.[type];
}

/** New objects of this object's type look like it (null: the built-in look again). */
export function setDefaultStyle(o: ObjectRecord, on: boolean): void {
  const ed = live.editor;
  if (!ed) return;
  ed.setDefaultStyle(o.type, on ? o.style : null);
  // persisted with the editor settings (the settings watcher hands them back to the editor)
  settings.editor = { ...settings.editor, defaultStyles: { ...ed.settings.defaultStyles } };
}
